import React from 'react';
import { motion } from 'framer-motion';
import { usePreloadImage } from '../../hooks/useResourceHints';
import type { CoverContent } from '../../types/advertorial';
interface CoverHeroProps {
  content: CoverContent;
}
export function CoverHero({
  content
}: CoverHeroProps) {
  const {
    mast,
    tag,
    kicker,
    headline,
    subheadline,
    byline,
    scrollCue,
    image
  } = content;
  usePreloadImage(image.src);
  return <header className="relative flex min-h-screen min-h-[100svh] w-full flex-col overflow-hidden bg-ink" data-id="element-44" data-mp="atponii8hmie">
      <img src={image.src} alt={image.alt} loading="eager" decoding="async" width={1600} height={900} className="absolute inset-0 h-full w-full object-cover object-[68%_center] opacity-60 md:opacity-70" data-id="element-45" data-mp="flpvzw0mvhp0" />
      <div aria-hidden="true" className="absolute inset-0 bg-ink/60 md:bg-[linear-gradient(90deg,rgba(8,9,11,0.94)_0%,rgba(8,9,11,0.7)_45%,rgba(8,9,11,0.25)_100%)]" data-id="element-46" data-mp="3o0rgx01davfs" />
      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[70%] bg-[linear-gradient(0deg,#0B0D10_6%,rgba(11,13,16,0.82)_38%,rgba(11,13,16,0.1)_88%,transparent_100%)]" data-id="element-47" data-mp="34wcb2noey1fw" />
      <div aria-hidden="true" className="grain-overlay absolute inset-0 opacity-60" data-id="element-48" data-mp="3dx21tcyth1mr" />

      {/* Cabecera de revista */}
      <div className="relative z-10 flex items-center justify-between border-b border-white/10 px-5 py-4 md:px-14 md:py-5" data-id="element-49" data-mp="38xfnt1b5zhcw">
        <span className="font-mono text-[9px] font-semibold tracking-[0.2em] text-gold md:text-[11px] md:tracking-[0.22em]" data-id="element-50" data-mp="2l96bqr3r67yy">
          {mast}
        </span>
        <span className="hidden font-mono text-[9.5px] tracking-[0.16em] text-ash/80 sm:block" data-id="element-51" data-mp="2uxwjykpdys6e">
          {tag}
        </span>
      </div>

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-5 py-10 text-center sm:px-8 md:px-10 md:py-14" data-id="element-52" data-mp="2q6oa5nk594sp">
        <motion.p initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="flex items-center gap-2.5 font-mono text-[9.5px] uppercase tracking-[0.22em] text-gold-soft sm:gap-3 md:text-[12px] md:tracking-[0.26em]" data-id="element-53" data-mp="22fiub8e7ecie">
          <span aria-hidden="true" className="h-px w-5 bg-gold-soft/55 md:w-8" data-id="element-54" data-mp="b4xh8rxluve8" />
          {kicker}
          <span aria-hidden="true" className="h-px w-5 bg-gold-soft/55 md:w-8" data-id="element-55" data-mp="g51zame5vy6k" />
        </motion.p>

        <motion.h1 initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.75,
        delay: 0.08,
        ease: 'easeOut'
      }} className="mt-5 max-w-5xl font-serif text-[29px] font-bold leading-[1.14] tracking-[-0.005em] text-parchment sm:text-[38px] md:mt-6 md:text-[52px] md:leading-[1.1] lg:text-[58px]" data-id="element-56" data-mp="3empf2myfa9kc">
          {headline.before}
          <span className="italic text-gold-soft" data-id="element-57" data-mp="13unyh9ujadek">{headline.accent}</span>
          {headline.after}
          <span className="mt-2.5 block font-serif text-[16px] font-normal italic text-ash md:mt-3 md:text-[22px]" data-id="element-58" data-mp="17v6pj1olrffp">
            {headline.paren}
          </span>
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.75,
        delay: 0.16,
        ease: 'easeOut'
      }} className="mt-5 max-w-2xl text-[14.5px] font-light leading-[1.62] text-[#AEB2B8] md:mt-6 md:text-[16.5px] md:leading-[1.65]" data-id="element-59" data-mp="2ocf92cc9qrew">
          {subheadline}
        </motion.p>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 font-mono text-[9px] tracking-[0.08em] text-ash/70 md:mt-8 md:gap-x-4 md:text-[10.5px]" data-id="element-60" data-mp="29okhiz9zyt3e">
          {byline.map((item, i) => <React.Fragment key={item} data-id="element-61" data-mp="3uzkig6qu4vaw" data-mpi={i}>
              {i > 0 && <span aria-hidden="true" className="h-[3px] w-[3px] rounded-full bg-white/25" data-id="element-62" data-mp="3q1sgsl0e1uti" />}
              <span data-id="element-63" data-mp="2mq3lm8acbjnb">{item}</span>
            </React.Fragment>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 0.45
      }} className="mt-9 flex flex-col items-center gap-2 font-mono text-[9px] tracking-[0.16em] text-ash/60 md:mt-12 md:text-[9.5px] md:tracking-[0.18em]" data-id="element-64" data-mp="2rq823yb90edj">
          <span data-id="element-65" data-mp="23zvk1umah198">{scrollCue}</span>
          <span aria-hidden="true" className="relative h-7 w-px overflow-hidden bg-white/10 md:h-8" data-id="element-66" data-mp="23ykepqd73lg0">
            <motion.span className="absolute left-0 top-0 block h-7 w-px bg-gold-soft md:h-8" animate={{
            y: ['-100%', '100%']
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-67" data-mp="28s5792w0rn7t" />
          </span>
        </motion.div>
      </div>
    </header>;
}